/**
 * The catalog table, sheet 04 callouts 7 and 8 (mission 6 WP10 T052; FR-020,
 * FR-027, NFR-007; ANA.6, ANA.7; style guide §7.7; research §1.21, §1.24;
 * post-tasks P6).
 *
 * Presentational: one row per rule in the order given (the API sorts by id,
 * hence `aria-sort` on the ID header). Every row renders; there is no pager
 * (P6, resolved: the catalog is one read, and `?rule=` must reach any row).
 * The ID cell holds the toggle, a button named by the mono rule id with
 * `aria-expanded` and `aria-controls`; the row has no handler and no role
 * (the nested-interactive rule). The one expanded row is followed by its
 * `RuleExplanation`, which alone reads GET /v1/rules/{id} (NFR-005). A
 * placeholder explanation carries register 11's short mark; a draft carries
 * nothing a customer sees.
 *
 * Under the explanation, 04/11 in full (mission 8 WP09 T049; FR-013; SAT.1;
 * amendment 13, OR-8; squad U5; research §1.15, §1.23): the expanded row, and
 * only it, reads `GET /v1/rules/{id}/latest-run`. On 200 it shows
 * `recentRunLink` to `/app/runs/<id>?rule=<rule>` (that run's drawer) and
 * `assistantCatalogLink` to the run with the panel open, the typed prefill
 * `{kind: 'rule', ruleId, count}` carried by `assistantNavState` (R14); on a
 * 404 (no ready run where the rule fired), while pending or on any failure,
 * neither.
 *
 * States: rows, loading (the Table skeleton rows), and no rows (`EmptyState`,
 * never an empty grid).
 */
import { Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SearchX } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { EmptyState } from '@/components/trellis/EmptyState'
import { RuleExplanation } from '@/components/trellis/RuleExplanation'
import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import type { RuleSummary } from '@/lib/api/rules'
import { assistantHref, assistantNavState } from '@/lib/assistant-panel'
import { Mono, count } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'
import { latestRunQuery } from '@/lib/queries'

const COLUMNS = 6

export interface CatalogTableProps {
  rows: RuleSummary[]
  loading: boolean
  /** The one expanded rule id, or null. */
  expanded: string | null
  onToggle(id: string): void
  /** Attached to the expanded rule's row, for the `?rule=` scroll. */
  rowRef(el: HTMLTableRowElement | null): void
  /** Every rule of the read, for the footer. */
  total: number
}

/** The expansion row's id; rule ids are `^[A-Z0-9-]+$`, so the id is safe as is. */
export function expansionId(ruleId: string): string {
  return `rule-explanation-${ruleId}`
}

const LINK = 'text-body-sm text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]'

/** 04/11 (OR-8): both links from the rule's latest ready run, or neither. */
function LatestRunLinks({ ruleId }: { ruleId: string }) {
  const latest = useQuery(latestRunQuery(ruleId))
  if (latest.data === undefined) return null
  const { run_id: runId, count: findings } = latest.data
  return (
    <p data-latest-run="" className="flex flex-wrap gap-x-6 gap-y-2">
      <Link to={`/app/runs/${encodeURIComponent(runId)}?rule=${encodeURIComponent(ruleId)}`} className={LINK}>
        {copyText('recentRunLink')}
      </Link>
      <Link to={assistantHref(runId)} state={assistantNavState({ kind: 'rule', ruleId, count: findings })} className={LINK}>
        {copyText('assistantCatalogLink')}
      </Link>
    </p>
  )
}

export function CatalogTable({ rows, loading, expanded, onToggle, rowRef, total }: CatalogTableProps) {
  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title={copyText('catalogNoMatchTitle')}
        description={copyText('catalogNoMatchBody')}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Severity</TableHead>
            <TableHead sort="ascending" className="w-36">
              ID
            </TableHead>
            <TableHead className="w-24">Segment</TableHead>
            <TableHead>Field</TableHead>
            <TableHead>Rule text</TableHead>
            <TableHead>CRRG reference</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableSkeletonRows columns={COLUMNS} />
          ) : (
            rows.map((rule) => {
              const open = rule.id === expanded
              const controls = expansionId(rule.id)
              return (
                <Fragment key={rule.id}>
                  <TableRow ref={open ? rowRef : undefined} data-state={open ? 'selected' : undefined}>
                    <TableCell>
                      <SeverityBadge severity={rule.default_severity} />
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        aria-expanded={open}
                        aria-controls={controls}
                        onClick={() => onToggle(rule.id)}
                        className="rounded-sm font-mono text-body-sm text-[var(--color-link)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                      >
                        {rule.id}
                      </button>
                    </TableCell>
                    <TableCell>{rule.segment}</TableCell>
                    <TableCell>
                      {rule.field ?? <span className="text-navy-600">—</span>}
                    </TableCell>
                    <TableCell className="max-w-[28rem]">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="min-w-0 truncate" title={rule.description}>
                          {rule.description}
                        </span>
                        {rule.explanation.status === 'placeholder' ? (
                          <Badge status="neutral">{copyText('placeholderMark')}</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Mono className="text-body-sm">{rule.crrg_ref}</Mono>
                    </TableCell>
                  </TableRow>
                  {open ? (
                    <tr id={controls} className="border-b border-navy-200 bg-white">
                      <td colSpan={COLUMNS} className="px-4 py-6">
                        <div className="flex flex-col gap-4">
                          <RuleExplanation ruleId={rule.id} />
                          <LatestRunLinks ruleId={rule.id} />
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })
          )}
        </TableBody>
      </Table>
      {loading ? null : (
        <p data-catalog-footer="" className="text-caption text-navy-600">
          Showing <Mono>{count(rows.length)}</Mono> of <Mono>{count(total)}</Mono> rules
        </p>
      )}
    </div>
  )
}
